import { useEffect } from 'react';
import { useFileBrowserStore } from './useFileBrowserStore';

// Keyboard shortcuts for the file browser (#5). Active only while browsing a
// site, and never while the user is typing in an input (search box, dialogs).
//
//   Delete        delete selection (opens the confirm dialog)
//   Ctrl/Cmd+A    select all
//   Esc           clear search, else clear selection
//   Backspace     navigate up one folder
//   F5            refresh current folder
//   Ctrl+Shift+N  new folder
export function useKeyboardShortcuts(): void {
  const {
    currentSiteId,
    selectedItems,
    searchQuery,
    isCreateFolderOpen,
    isDeleteConfirmOpen,
    moveDialogItems,
    currentPath,
    selectAll,
    clearSelection,
    setSearchQuery,
    navigateUp,
    refresh,
    setCreateFolderOpen,
    setDeleteConfirmOpen,
  } = useFileBrowserStore();

  useEffect(() => {
    const isTypingTarget = (target: EventTarget | null): boolean => {
      const el = target as HTMLElement | null;
      if (!el) return false;
      const tag = el.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable;
    };

    const onKeyDown = (e: KeyboardEvent) => {
      // Only in the browser view, never while typing or with a dialog open.
      if (!currentSiteId || isTypingTarget(e.target)) return;
      if (isCreateFolderOpen || isDeleteConfirmOpen || moveDialogItems) return;

      const ctrl = e.ctrlKey || e.metaKey;

      if (e.key === 'Delete' && selectedItems.size > 0) {
        e.preventDefault();
        setDeleteConfirmOpen(true);
      } else if (ctrl && !e.shiftKey && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        selectAll();
      } else if (e.key === 'Escape') {
        if (searchQuery) {
          setSearchQuery('');
        } else if (selectedItems.size > 0) {
          clearSelection();
        }
      } else if (e.key === 'Backspace' && currentPath) {
        e.preventDefault();
        navigateUp();
      } else if (e.key === 'F5') {
        e.preventDefault();
        refresh();
      } else if (ctrl && e.shiftKey && e.key.toLowerCase() === 'n') {
        e.preventDefault();
        setCreateFolderOpen(true);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    currentSiteId,
    selectedItems,
    searchQuery,
    isCreateFolderOpen,
    isDeleteConfirmOpen,
    moveDialogItems,
    currentPath,
    selectAll,
    clearSelection,
    setSearchQuery,
    navigateUp,
    refresh,
    setCreateFolderOpen,
    setDeleteConfirmOpen,
  ]);
}
